import { useCallback, useEffect, useMemo, useState } from "react";
import { useAlert } from "../../../../../../context/AlertContext";
import { saveConfigSection } from "../../../mock/estimateService.mock";

const clone = (v) => JSON.parse(JSON.stringify(v ?? null));

/**
 * Local editable copy of one config slice (e.g. "globalAssumptions").
 * Resets whenever the source changes (after a save + reload).
 *   const s = useSectionDraft(config, "surfaces", onSaved, "Surfaces");
 *   s.value / s.setValue / s.update(fn) / s.dirty / s.save() / s.reset()
 */
export default function useSectionDraft(config, sectionKey, onSaved, label) {
  const { showAlert } = useAlert();
  const source = config?.[sectionKey];
  const sourceJson = useMemo(() => JSON.stringify(source ?? null), [source]);
  const [value, setValue] = useState(() => clone(source));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(JSON.parse(sourceJson));
  }, [sourceJson]);

  const dirty = JSON.stringify(value) !== sourceJson;

  /** Immutable update helper: update((draft) => { draft.x = 1; }) on a deep clone. */
  const update = useCallback((mutator) => {
    setValue((cur) => {
      const next = clone(cur);
      mutator(next);
      return next;
    });
  }, []);

  const reset = useCallback(() => setValue(JSON.parse(sourceJson)), [sourceJson]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const draft = await saveConfigSection(sectionKey, value);
      showAlert({ type: "success", title: "Saved (mock)", message: `${label} saved to ${draft.label} (draft).` });
      await onSaved?.();
    } catch (e) {
      showAlert({ type: "error", message: e.message || "Save failed" });
    } finally {
      setSaving(false);
    }
  }, [sectionKey, value, label, onSaved, showAlert]);

  return { value, setValue, update, dirty, saving, save, reset };
}
